import { useEffect, useRef } from "react";
import { nswTileLayers, selectTileLayers, TRANSPARENT_TILE, usesEsri } from "../map/basemaps";
import { tileMeter } from "../map/tileMeter";
import { useAuth } from "../auth/AuthContext";
import {
  leafletGlobal,
  type LMap,
  type LMarker,
  type LPoint,
  type LPolyline,
} from "../map/leafletGlobal";

/** One flight as the map draws it: derived by the page from FlightMeta. */
export interface LocalityFlight {
  /** `${team}/${flightId}` — the same key the card list uses. */
  key: string;
  /** 1-based position in the section's card order (spec §7.4). */
  index: number;
  name: string;
  preview: [number, number][];
  bounds: [[number, number], [number, number]];
  href: string;
}

/** `side`: a tall sticky panel beside the card column (spec rev 2). `stacked`:
 *  a fixed-height strip above the cards, for sections too short to earn the
 *  column. The heights live in viewer-overrides.css. */
export type LocalityLayout = "side" | "stacked";

interface Props {
  flights: LocalityFlight[];
  /** Card under the pointer, or null. */
  hoveredKey: string | null;
  onHoverChange: (key: string | null) => void;
  onOpen: (href: string) => void;
  layout: LocalityLayout;
}

/** Eight saturated hues that read on satellite imagery. No greens or browns
 *  (they sink into vegetation and dirt) and no white, which is the halo colour
 *  drawn under every track. Ordered so that consecutive flights — the ones a
 *  reviewer is most likely to compare — never draw two neighbouring hues. */
export const TRACK_COLOURS = [
  "#ff3b30", // red
  "#ffd60a", // yellow
  "#32ade6", // sky blue
  "#ff6482", // salmon
  "#5856d6", // indigo
  "#ff9500", // orange
  "#bf5af2", // violet
  "#ff2d95", // magenta
];

export function trackColour(index: number): string {
  return TRACK_COLOURS[(index - 1) % TRACK_COLOURS.length] ?? TRACK_COLOURS[0]!;
}

/** `maxZoom` keeps a single short flight — or one whose bounds are a point —
 *  from fitting to street level. */
const FIT_OPTIONS = { padding: [24, 24] as [number, number], maxZoom: 17 };
/** Badges closer than this (in screen px) are stepped to the right until they
 *  clear; the badge is 22 px across. */
export const BADGE_MIN_GAP_PX = 24;
const BADGE_NUDGE_TRIES = 8;

export function unionBounds(
  flights: LocalityFlight[],
): [[number, number], [number, number]] | null {
  if (flights.length === 0) return null;
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  for (const f of flights) {
    south = Math.min(south, f.bounds[0][0]);
    west = Math.min(west, f.bounds[0][1]);
    north = Math.max(north, f.bounds[1][0]);
    east = Math.max(east, f.bounds[1][1]);
  }
  return [[south, west], [north, east]];
}

/** `L.tileLayer` options for one layer config: only the keys it sets. */
function tileOptions(layer: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of [
    "attribution", "subdomains", "minNativeZoom", "maxNativeZoom", "maxZoom", "tileSize",
    "zoomOffset",
  ]) {
    if (layer[k] !== undefined) out[k] = layer[k];
  }
  return out;
}

function badgeIcon(
  L: NonNullable<ReturnType<typeof leafletGlobal>>,
  index: number,
  colour: string,
): unknown {
  return L.divIcon({
    className: "vmc-locality-badge",
    html: `<span style="background:${colour}">${index}</span>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

/** The badge sits on the middle sample of the track. Chained recordings share
 *  start and end points, so a badge at the start would sit on the previous
 *  flight's end; the middle spreads them along the corridor. */
export function badgeAnchor(f: LocalityFlight): [number, number] {
  const mid = f.preview[Math.floor((f.preview.length - 1) / 2)];
  return mid ?? [f.bounds[0][0], f.bounds[0][1]];
}

interface Badge {
  marker: LMarker;
  anchor: [number, number];
}

function toLatLng(v: [number, number] | { lat: number; lng: number }): [number, number] {
  return Array.isArray(v) ? v : [v.lat, v.lng];
}

/** Keep badges from piling up where recordings are short: in index order,
 *  step any badge that lands within BADGE_MIN_GAP_PX of an earlier one to the
 *  right until it clears. Runs from the original anchors every time, so a
 *  zoom that separates them naturally puts them back on the track. */
export function separateBadges(map: LMap, badges: Badge[]): void {
  const placed: LPoint[] = [];
  for (const b of badges) {
    let p = map.latLngToContainerPoint(b.anchor);
    for (let i = 0; i < BADGE_NUDGE_TRIES; i++) {
      const clash = placed.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < BADGE_MIN_GAP_PX);
      if (!clash) break;
      p = { x: p.x + BADGE_MIN_GAP_PX, y: p.y };
    }
    placed.push(p);
    b.marker.setLatLng(toLatLng(map.containerPointToLatLng(p)));
  }
}

/** Satellite overview of every flight in a section (spec §7.2, revised
 *  2026-09-11): beside the cards as a tall sticky panel when the section has
 *  enough flights to fill a column, above them otherwise. */
export function LocalityMap(
  { flights, hoveredKey, onHoverChange, onOpen, layout }: Props,
): JSX.Element | null {
  const L = leafletGlobal();
  // The Esri key comes from GET /api/config via auth state; "" (paused, or an
  // offline sign-in) draws the keyless NSW imagery instead.
  const { esriApiKey } = useAuth();
  const hostRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LMap | null>(null);
  const linesRef = useRef<Map<string, LPolyline>>(new Map());
  // Latest callbacks, so the layer handlers bound at mount never go stale.
  const onHoverRef = useRef(onHoverChange);
  onHoverRef.current = onHoverChange;
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const all = unionBounds(flights);
  // Re-mount the map only when the set of flights changes, not on every render.
  const listKey = flights.map((f) => f.key).join("|");

  useEffect(() => {
    const host = hostRef.current;
    if (L === null || host === null || all === null) return undefined;
    // A wheel over the stacked strip should scroll the page; over the tall
    // side panel it zooms the map, as on any large map.
    const map = L.map(host, {
      zoomControl: false, attributionControl: true, scrollWheelZoom: layout === "side",
    });
    mapRef.current = map;
    const layers = selectTileLayers({
      online: true, playingFromCache: false, esriKey: esriApiKey,
    });
    const sat = layers.satellite as unknown as Record<string, unknown> & {
      labels?: Record<string, unknown>;
    };
    const satLayer = L.tileLayer(String(sat.url), tileOptions(sat));
    const labelsLayer = sat.labels !== undefined
      ? L.tileLayer(String(sat.labels.url), tileOptions(sat.labels))
      : null;
    // Tile guard (spec §8.2): count the paid provider's tiles, and finish the
    // session on the free NSW imagery once this page's budget is gone. The
    // labels overlay has no NSW equivalent, so it goes transparent. Subscribed
    // before addTo, like the shared viewer does, so the first tiles count too.
    let offTrip: (() => void) | null = null;
    if (usesEsri(layers)) {
      const degrade = (): void => {
        const nsw = nswTileLayers().satellite;
        // Both maps credit whoever is serving the tiles (NSW's CC BY).
        map.attributionControl?.removeAttribution(String(sat.attribution ?? ""));
        map.attributionControl?.addAttribution(nsw.attribution);
        satLayer.setUrl(nsw.url);
        labelsLayer?.setUrl(TRANSPARENT_TILE);
      };
      satLayer.on("tileloadstart", tileMeter.count);
      labelsLayer?.on("tileloadstart", tileMeter.count);
      offTrip = tileMeter.onTrip(degrade);
      // The trip fires once, so a map mounted after it (another section, or a
      // later visit to the page in this session) has to catch itself up.
      if (tileMeter.tripped()) degrade();
    }
    satLayer.addTo(map);
    labelsLayer?.addTo(map);
    const lines = new Map<string, LPolyline>();
    const badges: Badge[] = [];
    // Every halo first, so the next track's halo never paints over the end of
    // the one before it.
    for (const f of flights) {
      L.polyline(f.preview, {
        color: "#fff", weight: 7, opacity: 0.9, interactive: false,
      }).addTo(map);
    }
    for (const f of flights) {
      const colour = trackColour(f.index);
      const line = L.polyline(f.preview, { color: colour, weight: 4, opacity: 1 })
        .addTo(map)
        .bindTooltip(f.name, { sticky: true })
        .on("mouseover", () => onHoverRef.current(f.key))
        .on("mouseout", () => onHoverRef.current(null))
        .on("click", () => onOpenRef.current(f.href));
      const anchor = badgeAnchor(f);
      const marker = L.marker(anchor, { icon: badgeIcon(L, f.index, colour), keyboard: false })
        .addTo(map)
        .on("mouseover", () => onHoverRef.current(f.key))
        .on("mouseout", () => onHoverRef.current(null))
        .on("click", () => onOpenRef.current(f.href));
      lines.set(f.key, line);
      badges.push({ marker, anchor });
    }
    linesRef.current = lines;
    const fit = (): void => map.fitBounds(all, FIT_OPTIONS);
    const nudge = (): void => separateBadges(map, badges);
    fit();
    nudge();
    map.on("zoomend", nudge);
    // The side panel's height follows the viewport and a folded <details>
    // collapses the box; Leaflet only learns about either when told.
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        map.invalidateSize();
        fit();
        nudge();
      });
      observer.observe(host);
    }
    return () => {
      offTrip?.();
      observer?.disconnect();
      map.off("zoomend", nudge);
      map.remove();
      mapRef.current = null;
      linesRef.current = new Map();
    };
    // `all` and `flights` are derived from the same list as `listKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [L, listKey, layout, esriApiKey]);

  // Card hover → track emphasis (spec §7.2). The page keeps one hoveredKey for
  // every section, so a hover in another section must leave this map alone.
  useEffect(() => {
    const active = hoveredKey !== null && linesRef.current.has(hoveredKey) ? hoveredKey : null;
    for (const [key, line] of linesRef.current) {
      const hovered = key === active;
      line.setStyle({ weight: hovered ? 6 : 4, opacity: active === null || hovered ? 1 : 0.6 });
      if (hovered) line.bringToFront();
    }
  }, [hoveredKey, listKey]);

  if (L === null || all === null) return null;

  return (
    <div
      role="region"
      aria-label="Locality map"
      data-tour="flights.map"
      data-layout={layout}
      className={`vmc-locality-map vmc-locality-map--${layout}`}
    >
      <div ref={hostRef} style={{ position: "absolute", inset: 0 }} />
    </div>
  );
}
